import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Calendar, Bell, Clock, Check, Zap, MessageCircle, Users, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { siteData } from '../data/mock';

const AgendaDemo = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: Calendar,
      title: 'Agendamento 24h',
      description: 'Cliente agenda sozinho, a qualquer hora.'
    },
    {
      icon: Bell,
      title: 'Lembretes automáticos',
      description: 'WhatsApp 24h e 2h antes do horário.'
    },
    {
      icon: MessageCircle,
      title: 'Confirmação fácil',
      description: 'Cliente confirma ou reagenda com 1 clique.'
    }
  ];

  const benefits = [
    'Reduza faltas em até 80%',
    'Agenda sempre organizada',
    'Atendimento profissional',
    'Mais tempo para trabalhar'
  ];

  const templates = [
    { name: 'Barbearia', icon: '💈' },
    { name: 'Salão', icon: '💇' },
    { name: 'Pet Shop', icon: '🐕' },
    { name: 'Clínica', icon: '🏥' },
    { name: 'Estética', icon: '✨' },
    { name: 'Personalizado', icon: '⚙️' }
  ];

  const stats = [
    { value: '80%', label: 'Menos faltas' },
    { value: '24h', label: 'Disponível' },
    { value: '2min', label: 'Para agendar' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-black/5">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <img src={siteData.brand.logo} alt="AtivaJá" className="h-8" />
          </a>
          <Button
            onClick={() => navigate('/agenda/configurar')}
            className="btn-primary !py-2 !px-4 !min-h-0"
            data-testid="agenda-testar-gratis-btn"
          >
            Testar grátis
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 mb-6">
            <Calendar size={16} className="text-blue-600" />
            <span className="text-sm font-medium text-blue-700">Serviço AtivaJá</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            Agenda Cheia
          </h1>
          
          <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
            Acabou o cliente que marca e não aparece.
            <br />
            <strong className="text-slate-800">Lembretes automáticos no WhatsApp.</strong>
          </p>

          {/* Stats */}
          <div className="flex justify-center gap-8 mb-10">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-3xl font-bold text-blue-600">{stat.value}</p>
                <p className="text-sm text-slate-500">{stat.label}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/agenda/configurar')}
              className="btn-primary text-lg"
              data-testid="agenda-configurar-btn"
            >
              <Zap size={20} />
              Configurar minha agenda
              <ArrowRight size={20} />
            </Button>
            <Button
              onClick={() => navigate('/agenda/barbearia-do-ze')}
              variant="outline"
              className="btn-secondary text-lg"
              data-testid="agenda-demo-btn"
            >
              Ver demonstração
            </Button>
          </div>
        </div>
      </section>

      {/* Como funciona */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-slate-900 mb-12">
            Como funciona
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-sm border border-black/5 text-center">
                <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-6">
                  <feature.icon size={32} className="text-blue-600" />
                </div>
                <div className="text-sm font-medium text-blue-600 mb-2">Passo {index + 1}</div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problema vs Solução */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Problema */}
            <div className="bg-red-50 rounded-2xl p-8 border border-red-100">
              <h3 className="text-xl font-bold text-red-800 mb-6">😤 Sem Agenda Cheia</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-red-700">
                  <span className="text-red-400">✗</span>
                  Cliente marca e não aparece
                </li>
                <li className="flex items-start gap-3 text-red-700">
                  <span className="text-red-400">✗</span>
                  Você liga pra confirmar um por um
                </li>
                <li className="flex items-start gap-3 text-red-700">
                  <span className="text-red-400">✗</span>
                  Horário vago = dinheiro perdido
                </li>
                <li className="flex items-start gap-3 text-red-700">
                  <span className="text-red-400">✗</span>
                  Agenda bagunçada
                </li>
              </ul>
            </div>

            {/* Solução */}
            <div className="bg-green-50 rounded-2xl p-8 border border-green-100">
              <h3 className="text-xl font-bold text-green-800 mb-6">😎 Com Agenda Cheia</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-green-700">
                  <Check size={18} className="text-green-500 mt-0.5" />
                  Lembrete automático no WhatsApp
                </li>
                <li className="flex items-start gap-3 text-green-700">
                  <Check size={18} className="text-green-500 mt-0.5" />
                  Cliente confirma com 1 clique
                </li>
                <li className="flex items-start gap-3 text-green-700">
                  <Check size={18} className="text-green-500 mt-0.5" />
                  Reagendamento libera o horário
                </li>
                <li className="flex items-start gap-3 text-green-700">
                  <Check size={18} className="text-green-500 mt-0.5" />
                  Agenda sempre organizada
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Templates */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-slate-900 mb-4">
            Para qualquer tipo de negócio
          </h2>
          <p className="text-center text-slate-600 mb-12">
            Templates prontos ou personalize do zero
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {templates.map((template, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 border border-black/10 text-center hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
              >
                <div className="text-3xl mb-3">{template.icon}</div>
                <div className="text-sm font-medium text-slate-800">{template.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Preço */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">
                Nunca mais perca cliente por falta
              </h2>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <Check size={14} className="text-blue-600" />
                    </div>
                    <span className="text-slate-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-black/5">
              <div className="text-center mb-6">
                <div className="text-sm text-slate-500 mb-1">A partir de</div>
                <div className="text-4xl font-bold text-slate-900">
                  R$ 49<span className="text-lg font-normal text-slate-500">,99/mês</span>
                </div>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-sm text-slate-600">
                  <Check size={16} className="text-blue-600" /> Agendamentos ilimitados
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-600">
                  <Check size={16} className="text-blue-600" /> Lembretes WhatsApp
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-600">
                  <Check size={16} className="text-blue-600" /> Link exclusivo
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-600">
                  <Check size={16} className="text-blue-600" /> Painel de controle
                </li>
              </ul>
              <Button
                onClick={() => navigate('/agenda/configurar')}
                className="btn-primary w-full"
              >
                Começar agora
              </Button>
              <p className="text-xs text-center text-slate-500 mt-4">
                7 dias grátis para testar
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            Sua agenda pode estar funcionando em 5 minutos
          </h2>
          <p className="text-slate-600 mb-8">
            Configure uma vez. Compartilhe o link. Deixe o sistema trabalhar por você.
          </p>
          <Button
            onClick={() => navigate('/agenda/configurar')}
            className="btn-primary text-lg"
          >
            <Calendar size={20} />
            Ativar Agenda Cheia
            <ArrowRight size={20} />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-black/5">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src={siteData.brand.logo} alt="AtivaJá" className="h-6" />
            <span className="text-sm text-slate-500">Agenda Cheia</span>
          </div>
          <p className="text-sm text-slate-500">Um serviço AtivaJá © {new Date().getFullYear()}</p>
        </div>
      </footer>
    </div>
  );
};

export default AgendaDemo;
